<script>
  import { onMount } from 'svelte';
  import { select } from 'd3-selection';
  import { scaleLinear } from 'd3-scale';
  import { keywordsList } from './settings';
  import NotePercent from './NotePercent.svelte';
  import Icon from './Icon.svelte';
    import { construct_svelte_component } from 'svelte/internal';

  export let chartProps = {};
  export let data = [];
  let el;
  let tooltip;

  const minX = -0.05;
  const maxX = 1.05;
  let minY = 0.1;

  let iconSize = 40;

  const help = `Ogni pallino corrisponde a una <em>keyword</em> assegnata a uno o più articoli. La posizione orizzontale del pallini rappresenta la vicinanza degli articoli con quella <em>keyword</em> a una o all'altra delle nostre protagoniste (le keyword più a sinistra sono più comunemente associate a <em>Meloni</em>, quelle più a destra a <em>Schlein</em>); la posizione verticale indica invece il numero di articoli in cui abbiamo identificato quella <em>keyword</em>. Se un pallino appare più scuro, significa che più <em>keyword</em> hanno lo stesso numero di ricorrenze e la stessa "distanza" dalle candidate.`;

  const clean = term => term.replace(/[^a-zA-Z0-9]/ig, '');

  const getColor = d => {
    if (d.x > 0.8) {
      return 'var(--meloni)';
    }
    if (d.x > 0.6) {
      return 'var(--shade-meloni)';
    }
    if (d.x > 0.4) {
      return 'var(--overlap)';
    }
    if (d.x > 0.2) {
      return 'var(--shade-schlein)';
    }
    return 'var(--schlein)';
  };

  const draw = () => {
    const container = select(el);
    container.html('');

    const realMaxY = Math.max(...data.map(d => d.y));
    const maxY = Math.ceil(realMaxY * 1.1) ;
    const maxW = Math.max(...data.map(d => d.weight));
    minY = -(Math.ceil(maxY * 0.025));
    const scaleX = scaleLinear()
      .range([0, chartProps.width - chartProps.margins.left - chartProps.margins.right])
      .domain([maxX, minX]);
    const scaleY = scaleLinear()
      .range([0, chartProps.height - chartProps.margins.top - chartProps.margins.bottom ])
      .domain([maxY, minY]);
    const scaleW = scaleLinear()
      .range([0.3, 0.9])
      .domain([1, maxW]);
    
    const svg = container.append('svg')
      .attr('width', chartProps.width)
      .attr('height', chartProps.height)
      .attr('viewbox', `0 0 ${chartProps.width} ${chartProps.height}`);

    // Headers
    const gh = svg.append('g')
    .attr('transform', `translate(${chartProps.margins.left}, 0)`);
    // Grid
    const gg = svg.append('g')
      .attr('transform', `translate(${chartProps.margins.left}, ${chartProps.margins.top})`);
    // Notes
    const gn = svg.append('g')
      .attr('transform', `translate(${chartProps.margins.left}, ${chartProps.margins.top})`);
    // Legend
    const gl = svg.append('g')
      .attr('transform', `translate(${chartProps.margins.left}, ${chartProps.height - chartProps.margins.bottom + chartProps.margins.top + 5})`);
    // Points 
    const gp = svg.append('g')
      .attr('transform', `translate(${chartProps.margins.left}, ${chartProps.margins.top})`);
    
    // Grid by values
    gg.append('line')
      .attr('x1', scaleX(0.2))
      .attr('x2', scaleX(0.2))
      .attr('y1', scaleY(minY))
      .attr('y2', scaleY(maxY))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(0.4))
      .attr('x2', scaleX(0.4))
      .attr('y1', scaleY(minY))
      .attr('y2', scaleY(maxY))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(0.6))
      .attr('x2', scaleX(0.6))
      .attr('y1', scaleY(minY))
      .attr('y2', scaleY(maxY))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(0.8))
      .attr('x2', scaleX(0.8))
      .attr('y1', scaleY(minY))
      .attr('y2', scaleY(maxY))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(minX))
      .attr('x2', scaleX(minX))
      .attr('y1', scaleY(minY))
      .attr('y2', scaleY(maxY))
      .style('stroke-width', 1)
      .style('stroke', 'var(--dark)');

    gg.append('line')
      .attr('x1', scaleX(maxX))
      .attr('x2', scaleX(maxX))
      .attr('y1', scaleY(minY))
      .attr('y2', scaleY(maxY))
      .style('stroke-width', 1)
      .style('stroke', 'var(--dark)');

    gg.append('text')
      .attr('x', scaleX(minX))
      .attr('y', scaleY(minY))
      .attr('dy', 5)
      .style('text-anchor', 'middle')
      .style('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text('0 occorrenze');
    gg.append('text')
      .attr('x', scaleX(maxX))
      .attr('y', scaleY(minY))
      .attr('dy', 5)
      .style('text-anchor', 'middle')
      .style('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text('0 occorrenze');
    gg.append('text')
      .attr('x', scaleX(maxX))
      .attr('y', scaleY(maxY))
      .attr('dy', -5)
      .style('text-anchor', 'middle')
      .style('dominant-baseline', 'baseline')
      .attr('class', 'points--label')
      .text(`${maxY} occorrenze`);
    gg.append('text')
      .attr('x', scaleX(minX))
      .attr('y', scaleY(maxY))
      .attr('dy', -5)
      .style('text-anchor', 'middle')
      .style('dominant-baseline', 'baseline')
      .attr('class', 'points--label')
      .text(`${maxY} occorrenze`);

    // Notes
    gn.append('text')
      .attr('x', scaleX(0.5))
      .attr('y', scaleY(minY))
      .attr('dy', 5)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text('↓ Keyword meno comuni ↓');
    gn.append('text')
      .attr('x', scaleX(0.5))
      .attr('y', scaleY(maxY))
      .attr('dy', -5)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'baseline')
      .attr('class', 'points--label')
      .text('↑ Keyword più comuni ↑');
    gn.append('text')
      .attr('transform', `translate(-14, ${chartProps.height / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text('↑ Keyword associate più spesso a Meloni ↑');
    gn.append('text')
      .attr('transform', `translate(${chartProps.width - chartProps.margins.right - chartProps.margins.left + 14}, ${chartProps.height / 2}) rotate(90)`)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text('↑ Keyword associate più spesso a Schlein ↑');

    // Data
    gp.selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', d=> scaleX(d.x))
      .attr('cy', d=> scaleY(d.y))
      .attr('r', chartProps.radius)
      .attr('id', d => `cc-${clean(d.term)}`)
      .style('fill', getColor)
      .style('stroke', 'none')
      .style('stroke-width', 0)
      .style('opacity', d => scaleW(d.weight))
      .style('mix-blend-mode', 'multiply')
      .on('mouseenter', (e, d) => {
        select(`#cc-${clean(d.term)}`)
          .attr('class', 'selected');
        select(tooltip)
          .html(`<strong>${keywordsList(d.term)}</strong>Count: ${d.y}<br />Meloni: ${d.meloni}<br />Schlein: ${d.schlein}`)
          .style('top', `${scaleY(d.y) + chartProps.margins.top + chartProps.radius}px`)
          .style('left', `${scaleX(d.x) + chartProps.margins.left}px`)
          .attr('class', `tooltip ${(scaleX(d.x) + chartProps.margins.left) > (chartProps.width / 2) ? 'left' : 'right'}`);
      })
      .on('mouseout', (e, d) => {
        select(`#cc-${clean(d.term)}`)
          .attr('class', '');
        select(tooltip)
          .attr('class', 'tooltip')
          .html('');
      });

    gp.selectAll('text')
      .data(data.filter(d => d.y === realMaxY))
      .enter()
      .append('text')
      .attr('x', d=> scaleX(d.x))
      .attr('y', d=> scaleY(d.y))
      .attr('dy', (chartProps.radius + 2))
      .attr('dx', d => {
        if (d.x < maxX / 3) return -(chartProps.radius);
        if (d.x > ((maxX / 3) * 2)) return chartProps.radius;
        return 0;
      })
      .attr('class', 'points--label')
      .attr('id', d => `tt-${clean(d.term)}`)
      .style('dominant-baseline', 'hanging')
      .style('text-anchor', d => {
        if (d.x < maxX / 3) return 'start';
        if (d.x > ((maxX / 3) * 2)) return 'end';
        return 'middle';
      })
      .style('fill', getColor)
      .style('opacity', 1)
      .style('stroke', 'none')
      .text(d => d.term);

    const baseX = 16;
    const ll = [ 1, (maxW / 3), (maxW / 3 * 2), maxW ];
    ll.forEach((l, i) => {
      gl.append('circle')
        .attr('cx', baseX + chartProps.radius + (chartProps.radius * i))
        .attr('cy', chartProps.radius / 2)
        .attr('r', chartProps.radius)
        .style('fill', 'var(--neutral)')
        .style('stroke', 'var(--light)')
        .style('stroke-width', 1)
        .style('mix-blend-mode', 'multiply')
        .style('opacity', d => scaleW(l));
    });
    gl.append('text')
      .attr('x', 0)
      .attr('y', chartProps.radius / 2)
      .attr('class', 'points--label')
      .style('text-anchor', 'start')
      .style('dominant-baseline', 'middle')
      .text(1);
    gl.append('text')
      .attr('x', ll.length * chartProps.radius + baseX * 2)
      .attr('y', chartProps.radius / 2)
      .attr('class', 'points--label')
      .style('text-anchor', 'start')
      .style('dominant-baseline', 'middle')
      .text(`${maxW} Keyword con gli stessi valori`);
  };  

  onMount(() => {
    if (chartProps?.width > 0 && chartProps?.height > 0 && data.length) {
      draw();
    }
  });

  $: if (chartProps?.width > 0 && chartProps?.height > 0 && data.length) {
     draw();
  };
</script>

<div id="pointsHorizontal" style="height: {chartProps.height}px">
  <div class="chart" bind:this={el}></div>
  <div class="tooltip" bind:this={tooltip} />
  <div class="icon meloni"><Icon sentiment="meloni" src="meloni" /></div>
  <div class="icon schlein"><Icon sentiment="schlein" src="schlein" /></div>
</div>
<NotePercent text={help} />

<style>
  #pointsHorizontal {
    cursor: crosshair;
    display: block;
    position: relative;
    width: 100%;
  }

  .chart {
    bottom: 0;
    display: block;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    z-index: 2;
  }

  .icon {
    display: none;
  }

  @media screen and (min-width:1024px) {
    .icon {
      display: block;
      height: 80px;
      left: 0;
      position: absolute;
      top: 50%;
      transform: translate3d(-30px, -50%, 0);
      width: 80px;
      z-index: 3;
    }

    .icon.schlein {
      left: auto;
      right: 0;
      transform: translate3d(30px, -50%, 0);
    }
  }

  @media screen and (min-width:1280px) {
    .icon {
      display: block;
      height: 120px;
      left: 0;
      position: absolute;
      top: 50%;
      transform: translate3d(-60px, -50%, 0);
      width: 120px;
      z-index: 3;
    }

    .icon.schlein {
      left: auto;
      right: 0;
      transform: translate3d(60px, -50%, 0);
    }
  }
</style>